/**
 * Green Flag — Match page
 *
 * Full-screen page for submitting matches from a past spark. Reached from
 * the MatchFillWidget on the Events page (`navigate("/event/:sparkId/match")`).
 *
 * Two layouts — the user toggles between them and we remember the choice
 * in localStorage (see `useMatchViewPreference`):
 *
 *   - "profile" — one profile at a time, full detail: large photo, name,
 *     dating intention, core values.
 *   - "grid" — a 2-column grid of square photo tiles with Like buttons.
 *
 * Navigating in from the widget carries `state.focusUserId` so we scroll
 * to / highlight the specific candidate the user tapped. The incoming
 * card's framer-motion `layoutId` matches the destination's first image,
 * so the cross-route transition morphs between them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { store } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { markShouldPromptDoneMatching } from "../../../lib/donePromptFlags";
import { Box, Input, Stack, chakra } from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  SparkParticipantStatus,
  UserRelationshipType,
  type SparkParticipant,
  type UserProfile,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../../lib/apiReq";
type RelationshipResult = {
  userId: number;
  sparkId: number | null;
  status: SparkParticipantStatus | null;
  // Set by the server when this like completed a two-way match. Drives the
  // mutual-spark celebration toast.
  mutualMatch?: boolean;
};
import { useStoreData } from "../../../lib/store/store";
import { isLoggedIn } from "../../../lib/isLoggedIn";
import {
  MediaBlobImage,
  primaryProfileImagePath,
} from "../../../lib/profileMedia";
import { Icon } from "../../../components/Icon/Icon";
import { ConfettiFlagEmptyState } from "../../../components/ConfettiFlagEmptyState/ConfettiFlagEmptyState";
import { useMatchCelebration } from "../../../components/MatchNotification/useMatchCelebration";
import {
  PillTabs,
  type PillTabKey,
} from "../../../components/PillTabs/PillTabs";
import {
  AGE_MAX,
  AGE_MIN,
  useMatchPreferences,
  type MatchPreferences,
  type MatchViewMode,
} from "./useMatchViewPreference";
import {
  MatchProfile,
  type MatchProfileDecision,
} from "./MatchProfile";
import {
  CompleteProfileGate,
  isProfileCompleteForMatching,
} from "../../../components/CompleteProfileGate/CompleteProfileGate";
import { ModalPage } from "../../../components/ModalPage";

const MChakraDiv = motion.create(chakra.div);

export const Match = () => {
  const navigate = useNavigate();
  const { sparkId: sparkIdParam } = useParams<{ sparkId: string }>();
  const location = useLocation();
  const storeData = useStoreData();
  const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);

  // Seed the age range with the user's profile preferences so the modal opens
  // on their existing dating range. The filter still starts disabled — this
  // only affects where the sliders sit when the user first toggles it on.
  const defaultAgeRange = useMemo<[number, number]>(
    () => [
      storeData.user?.min_age_preference ?? AGE_MIN,
      storeData.user?.max_age_preference ?? AGE_MAX,
    ],
    [storeData.user?.min_age_preference, storeData.user?.max_age_preference]
  );
  const [preferences, updatePreferences] = useMatchPreferences(defaultAgeRange);
  const { viewMode, ageFilterEnabled, ageRange } = preferences;
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const friendsOnly = storeData.user?.friends_only ?? false;
  const requestedTab = readTabParam(searchParams.get("tab"));
  const tab = friendsOnly ? "friends" : requestedTab;
  const setTab = useCallback(
    (next: PillTabKey) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "dating") params.delete("tab");
          else params.set("tab", next);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [query, setQuery] = useState("");
  const [previewProfile, setPreviewProfile] = useState<UserProfile | null>(null);
  // Profile-mode override: when the user picks someone from the search
  // results dropdown we pin that profile as the focus instead of stepping
  // through the undecided queue. Cleared whenever the underlying candidate
  // set shifts (tab / age filter) so we don't show a stale person.
  const [pinnedProfile, setPinnedProfile] = useState<UserProfile | null>(null);
  const [relationshipReq] = useReq<RelationshipResult>(apiReq);
  const { celebrate, matchCelebration } = useMatchCelebration();

  const entryState = location.state as
    | {
        focusUserId?: number;
        animate?: boolean;
        pendingDecision?: { userId?: number; decision: "match" | "pass" };
      }
    | null;
  // Pin a specific attendee to the front of the list — first card in profile
  // mode, top-left tile in grid mode. In-app navigation passes this via
  // `location.state.focusUserId` (e.g. MatchFillWidget). Cold loads from
  // outside the app (notification emails, deep links) pass it via the
  // `?focus=<userId>` query param, which survives a hard navigation.
  const focusFromQuery = useMemo(() => {
    const raw = searchParams.get("focus");
    if (!raw) return undefined;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? undefined : parsed;
  }, [searchParams]);
  const focusUserId = entryState?.focusUserId ?? focusFromQuery;
  // Only animate the modal when we arrived from a navigation that opted
  // in (currently: the MatchFillWidget). A fresh page load / refresh
  // renders statically so there's no scale-in flash on mount.
  const animateEntrance = entryState?.animate === true;

  // Freeze sparkId at its first valid value. During AnimatePresence exit the
  // route no longer matches `/event/:sparkId/match`, so useParams returns an
  // empty object — without this, the component would fall into the `isNaN`
  // early-return branch mid-exit, swapping out the motion wrapper and killing
  // the exit animation.
  const sparkIdRef = useRef<number>(
    sparkIdParam ? parseInt(sparkIdParam, 10) : NaN
  );
  if (sparkIdParam) {
    const parsed = parseInt(sparkIdParam, 10);
    if (!isNaN(parsed)) sparkIdRef.current = parsed;
  }
  const sparkId = sparkIdRef.current;
  const pastSpark = useMemo(
    () => storeData.pastSparks?.find((s) => s.id === sparkId) ?? null,
    [storeData.pastSparks, sparkId]
  );

  // "Likes Me" is only meaningful when at least one attendee has liked the
  // viewer. When empty, we hide the tab and bounce the user back to Dating
  // if they landed here via a deep link.
  const hasLikes = useMemo(
    () => pastSpark?.participants.some((p) => p.likedByThem) ?? false,
    [pastSpark?.participants]
  );
  const hiddenTabs = useMemo<PillTabKey[]>(
    () =>
      friendsOnly
        ? ["dating", "likesMe"]
        : hasLikes
          ? []
          : ["likesMe"],
    [friendsOnly, hasLikes]
  );

  // Narrow to the active tab. "dating" / "friends" filter by category;
  // "likesMe" shows everyone who has liked the viewer, regardless of
  // category or whether the viewer has responded. Age filter — when
  // enabled — drops anyone whose age falls outside [min, max]; participants
  // with unknown age are hidden rather than silently included.
  const participants = useMemo<SparkParticipant[]>(() => {
    const [minAge, maxAge] = ageRange;
    const list =
      pastSpark?.participants.filter((p: SparkParticipant) => {
        if (tab === "likesMe") {
          if (!p.likedByThem) return false;
        } else if (p.category !== (tab === "dating" ? "dating" : "friend")) {
          return false;
        }
        if (ageFilterEnabled) {
          if (p.age == null) return false;
          if (p.age < minAge || p.age > maxAge) return false;
        }
        return true;
      }) ?? [];
    if (!focusUserId) return list;
    const idx = list.findIndex((c) => c.id === focusUserId);
    if (idx <= 0) return list;
    return [list[idx], ...list.slice(0, idx), ...list.slice(idx + 1)];
  }, [
    pastSpark?.participants,
    focusUserId,
    tab,
    ageFilterEnabled,
    ageRange,
  ]);

  const searchedParticipants = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return participants;
    return participants.filter((c) =>
      c.firstName?.toLowerCase().includes(q)
    );
  }, [participants, query]);

  // Profile view steps through undecided profiles only — no point asking
  // the user to re-decide what they already chose.
  const profileViewCandidates = useMemo(
    () => participants.filter((p) => p.status === null),
    [participants]
  );

  // Drop the pinned profile if it falls out of the current candidate set
  // (tab switch, age-filter toggle, etc.) so we never render a stale person.
  useEffect(() => {
    if (!pinnedProfile) return;
    if (!participants.some((p) => p.id === pinnedProfile.id)) {
      setPinnedProfile(null);
    }
  }, [participants, pinnedProfile]);

  // The stored dating/friend category for a given participant. Read per
  // person rather than from the active tab, because the "Likes Me" tab mixes
  // both. Falls back to the viewer's own mode for anyone not in the list —
  // a Friends Only viewer has no dating context to fall back to.
  const categoryOf = useCallback(
    (profile: { id: number }): "dating" | "friend" =>
      pastSpark?.participants.find((p) => p.id === profile.id)?.category ??
      (friendsOnly ? "friend" : "dating"),
    [pastSpark?.participants, friendsOnly]
  );

  const applyRelationship = useCallback(
    async (profile: UserProfile, relationship: UserRelationshipType) => {
      if (isNaN(sparkId)) return;
      const response = await relationshipReq.put("/user/relationship", {
        userId: profile.id,
        sparkId,
        relationship,
      });
      if (!response.success) return;
      store.dispatch({
        type: ActionType.UpdateParticipantStatus,
        params: { sparkId, userId: profile.id, status: response.result.status },
      });
      if (response.result.mutualMatch) {
        celebrate(profile, categoryOf(profile));
      }
    },
    [relationshipReq, sparkId, celebrate, categoryOf]
  );

  // Undo a past-spark Like/Friend tap: hard-delete the row so status
  // reverts to undecided (null) rather than leaving a NoSpark artifact.
  const clearRelationship = useCallback(
    async (profile: UserProfile) => {
      if (isNaN(sparkId)) return;
      const response = await relationshipReq.delete(
        `/user/relationship/${sparkId}/${profile.id}`,
        {}
      );
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateParticipantStatus,
          params: {
            sparkId,
            userId: profile.id,
            status: response.result.status,
          },
        });
      }
    },
    [relationshipReq, sparkId]
  );

  // Flag this spark to show the "Are you done matching?" prompt on the
  // Events page. Only fires when the user is in grid mode — profile mode
  // exhausts the queue on its own, so we don't need to ask. Re-entering
  // the grid after tapping "Not yet" re-sets the flag, which is the signal
  // that the user wasn't actually done.
  useEffect(() => {
    if (isNaN(sparkId)) return;
    if (viewMode !== "grid") return;
    markShouldPromptDoneMatching(sparkId);
  }, [sparkId, viewMode]);

  // Friends Only has a single matching context, so normalize every deep link
  // to Friends. Dating users bounce off "Likes Me" when nobody has liked them.
  // Gate on `pastSpark` being loaded so initial store hydration cannot clobber
  // a legitimate deep-link parameter.
  useEffect(() => {
    if (!pastSpark) return;
    if (friendsOnly) {
      if (requestedTab !== "friends") setTab("friends");
      return;
    }
    if (requestedTab === "likesMe" && !hasLikes) setTab("dating");
  }, [pastSpark, friendsOnly, requestedTab, hasLikes, setTab]);

  // Profile-view FABs: heart → Spark, heart-broken → NoSpark.
  const handleDecision = useCallback(
    (profile: UserProfile, decision: MatchProfileDecision) =>
      applyRelationship(
        profile,
        decision === "match"
          ? UserRelationshipType.Spark
          : UserRelationshipType.NoSpark
      ),
    [applyRelationship]
  );

  // If the user arrived here with a pending decision from the MatchFillWidget
  // (they clicked like/pass while their profile was incomplete), apply it as
  // soon as the CompleteProfileGate closes — i.e. once their profile is
  // actually ready to record relationships. The ref gate ensures we only
  // fire once per navigation, even as the store re-renders.
  const pendingDecision = entryState?.pendingDecision;
  const pendingDecisionSentRef = useRef(false);
  useEffect(() => {
    if (pendingDecisionSentRef.current) return;
    if (!pendingDecision?.userId) return;
    if (!isProfileCompleteForMatching(storeData.user)) return;
    const target = pastSpark?.participants.find(
      (p) => p.id === pendingDecision.userId
    );
    if (!target) return;
    pendingDecisionSentRef.current = true;
    void applyRelationship(
      target,
      pendingDecision.decision === "match"
        ? UserRelationshipType.Spark
        : UserRelationshipType.NoSpark
    );
    // Clear the state so a refresh or back-nav doesn't replay the decision.
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...entryState, pendingDecision: undefined },
    });
  }, [
    pendingDecision,
    storeData.user,
    pastSpark?.participants,
    applyRelationship,
    navigate,
    location.pathname,
    location.search,
    entryState,
  ]);

  // Grid-tile click: transition depends on current status.
  //   null / NoSpark    → Spark    (PUT)
  //   Spark  (one-way)  → cleared  (DELETE)
  //   MutualSpark       → open match detail page
  const handleTileAction = useCallback(
    (participant: SparkParticipant) => {
      if (participant.status === SparkParticipantStatus.MutualSpark) {
        navigate(`/matches/${participant.id}`);
        return;
      }
      if (participant.status === SparkParticipantStatus.Spark) {
        void clearRelationship(participant);
        return;
      }
      void applyRelationship(participant, UserRelationshipType.Spark);
    },
    [applyRelationship, clearRelationship, navigate]
  );

  // Auth + data sanity.
  useEffect(() => {
    if (!storeData.loading && !loggedIn) {
      navigate(
        `/sign-in?r=${encodeURIComponent(location.pathname + location.search)}`,
        { replace: true }
      );
    }
  }, [
    loggedIn,
    storeData.loading,
    navigate,
    location.pathname,
    location.search,
  ]);

  if (isNaN(sparkId)) {
    return (
      <ModalPage
        motionKey="match-modal"
        title="Submit Your Matches"
        fallback="/events"
        bodyBg="bg.canvas"
      >
        <Box px="4" py="8" textAlign="center" color="fg.muted">
          Invalid match link.
        </Box>
      </ModalPage>
    );
  }

  if (!pastSpark && !storeData.loading) {
    return (
      <ModalPage
        motionKey="match-modal"
        title="Submit Your Matches"
        fallback="/events"
        bodyBg="bg.canvas"
      >
        <Box px="4" py="8" textAlign="center" color="fg.muted">
          This event's matching window has closed.
        </Box>
      </ModalPage>
    );
  }

  return (
    <>
    <ModalPage
      motionKey="match-modal"
      title="Submit Your Matches"
      fallback="/events"
      animateEntrance={animateEntrance}
      bodyBg="bg.canvas"
      stickyBelowHeader={
        <Box px="4" pt="4" pb="4" bg="bg.canvas" w="100%" maxW="40rem" mx="auto">
          <Stack gap="3" direction="row" align="center">
            <Box flex="1" position="relative">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder=""
                borderRadius="full"
                h="44px"
                pl="5"
                pr="12"
                bg="bg.canvas"
                borderColor="border.subtle"
              />
              <Box
                position="absolute"
                right="4"
                top="0"
                h="44px"
                display="flex"
                alignItems="center"
                color="fg.muted"
                pointerEvents="none"
              >
                <Icon name="search" boxSize="16px" />
              </Box>
              {viewMode === "profile" && query.trim() && (
                <SearchResultsDropdown
                  results={searchedParticipants}
                  onPick={(profile) => {
                    setPinnedProfile(profile);
                    setQuery("");
                  }}
                />
              )}
            </Box>
            <chakra.button
              type="button"
              aria-label="Open preferences"
              onClick={() => setPreferencesOpen(true)}
              w="44px"
              h="44px"
              borderRadius="full"
              bg={ageFilterEnabled ? "bg.brand" : "bg.canvas"}
              borderWidth="1px"
              borderColor={ageFilterEnabled ? "bg.brand" : "border.subtle"}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={ageFilterEnabled ? "fg.onBrand" : "fg.default"}
              cursor="pointer"
            >
              <Icon name="sliders" boxSize="16px" />
            </chakra.button>
          </Stack>

          <Box mt="4">
            <PillTabs value={tab} onChange={setTab} hiddenKeys={hiddenTabs} />
          </Box>
        </Box>
      }
    >
        <Box
          data-testid="match-page-body"
          px={viewMode === "grid" ? "4" : "0"}
          pb={viewMode === "profile" ? "0" : "10"}
          flex={viewMode === "profile" ? "1" : undefined}
          display={viewMode === "profile" ? "flex" : undefined}
          flexDirection={viewMode === "profile" ? "column" : undefined}
          w="100%"
          maxW="40rem"
          mx="auto"
        >
          {tab !== "likesMe" && viewMode === "profile" ? (
            pinnedProfile ? (
              <MatchProfile
                profile={pinnedProfile}
                category={categoryOf(pinnedProfile)}
                onDecision={(d) => {
                  handleDecision(pinnedProfile, d);
                  setPinnedProfile(null);
                }}
              />
            ) : profileViewCandidates.length === 0 ? (
              <ConfettiFlagEmptyState
                headline="Your Work Here Is Done."
                body="You showed up, you vibed, you swiped. The ball's in the universe's court now."
              />
            ) : (
              <ProfileView
                candidates={profileViewCandidates}
                categoryOf={categoryOf}
                onDecision={handleDecision}
              />
            )
          ) : searchedParticipants.length === 0 ? (
            <Box textAlign="center" color="fg.muted" py="10">
              {query
                ? "No matches for that search."
                : tab === "likesMe"
                  ? "Nobody's liked you at this event yet."
                  : "Nobody to show here."}
            </Box>
          ) : (
            <GridView
              participants={searchedParticipants}
              onOpenProfile={setPreviewProfile}
              onTileAction={handleTileAction}
            />
          )}
        </Box>
      {previewProfile && (
        <ProfilePreviewModal
          profile={previewProfile}
          category={categoryOf(previewProfile)}
          onClose={() => setPreviewProfile(null)}
          onDecision={(decision) => {
            handleDecision(previewProfile, decision);
            setPreviewProfile(null);
          }}
        />
      )}
      {preferencesOpen && (
        <PreferencesModal
          preferences={preferences}
          onChange={updatePreferences}
          onClose={() => setPreferencesOpen(false)}
        />
      )}
      <CompleteProfileGate />
    </ModalPage>
    {matchCelebration}
    </>
  );
};

// Tab is carried in `?tab=dating|friends|likesMe` so deep links and browser
// back/forward preserve the view. Missing/unknown values fall back to
// "dating" (the default), and the default is never serialized back into the
// URL to keep it clean.
function readTabParam(raw: string | null): PillTabKey {
  if (raw === "friends" || raw === "likesMe") return raw;
  return "dating";
}

const modalVariants = {
  initial: { opacity: 0, scale: 0.92 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" as const },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.15, ease: "easeIn" as const },
  },
};

// ---------------------------------------------------------------------------
// Search results dropdown — profile-mode only.
// ---------------------------------------------------------------------------

function SearchResultsDropdown({
  results,
  onPick,
}: {
  results: SparkParticipant[];
  onPick: (profile: SparkParticipant) => void;
}) {
  return (
    <Box
      position="absolute"
      top="calc(44px + 6px)"
      left="0"
      right="0"
      bg="bg.canvas"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="16px"
      boxShadow="lg"
      maxH="320px"
      overflowY="auto"
      zIndex="20"
    >
      {results.length === 0 ? (
        <Box px="4" py="3" fontSize="sm" color="fg.muted">
          No matches for that search.
        </Box>
      ) : (
        results.map((p) => {
          const hero = primaryProfileImagePath(p);
          return (
            <chakra.button
              key={p.id}
              type="button"
              onClick={() => onPick(p)}
              w="full"
              display="flex"
              alignItems="center"
              gap="3"
              px="3"
              py="2"
              bg="bg.canvas"
              textAlign="left"
              cursor="pointer"
              transition="background 120ms ease"
              _hover={{ bg: "bg.subtle" }}
              _focusVisible={{ bg: "bg.subtle", outline: "none" }}
            >
              <Box
                w="40px"
                h="40px"
                borderRadius="full"
                overflow="hidden"
                bg="border.subtle"
                flexShrink={0}
              >
                <MediaBlobImage
                  path={hero}
                  alt={p.firstName ?? "Profile"}
                  css={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  fallback={<Icon name="user-solid" boxSize="50%" />}
                />

              </Box>
              <Box fontSize="sm" fontWeight="semibold" color="fg.default">
                {p.firstName}
                {p.age != null ? `, ${p.age}` : ""}
              </Box>
            </chakra.button>
          );
        })
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Profile view — one profile at a time, scrollable.
// ---------------------------------------------------------------------------

function ProfileView({
  candidates,
  categoryOf,
  onDecision,
}: {
  candidates: UserProfile[];
  categoryOf: (profile: { id: number }) => "dating" | "friend";
  onDecision: (profile: UserProfile, decision: MatchProfileDecision) => void;
}) {
  const focus = candidates[0];
  const next = candidates[1] ?? null;
  if (!focus) return null;
  return (
    <MatchProfile
      profile={focus}
      category={categoryOf(focus)}
      behindProfile={next}
      onDecision={(d) => onDecision(focus, d)}
    />
  );
}

// ---------------------------------------------------------------------------
// Grid view — 2-column photo tiles with Like buttons.
// ---------------------------------------------------------------------------

function GridView({
  participants,
  onOpenProfile,
  onTileAction,
}: {
  participants: SparkParticipant[];
  onOpenProfile: (profile: UserProfile) => void;
  onTileAction: (participant: SparkParticipant) => void;
}) {
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(2, 1fr)"
      gap="3"
    >
      {participants.map((p) => (
        <GridTile
          key={p.id}
          participant={p}
          onOpenProfile={onOpenProfile}
          onTileAction={onTileAction}
        />
      ))}
    </Box>
  );
}

function GridTile({
  participant,
  onOpenProfile,
  onTileAction,
}: {
  participant: SparkParticipant;
  onOpenProfile: (profile: UserProfile) => void;
  onTileAction: (participant: SparkParticipant) => void;
}) {
  const hero = primaryProfileImagePath(participant);
  const { status, category } = participant;
  const isFilled =
    status === SparkParticipantStatus.Spark ||
    status === SparkParticipantStatus.MutualSpark;
  const tone = category === "dating"
    ? { bg: "bg.brand", fg: "fg.brand", border: "border.brand" }
    : { bg: "bg.friends", fg: "fg.friends", border: "border.friends" };
  const label = tileButtonLabel(status, category);
  const icon = tileButtonIcon(status, category);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="12px"
      overflow="hidden"
      bg="bg.canvas"
    >
      <MChakraDiv
        onClick={() => onOpenProfile(participant)}
        css={{
          position: "relative",
          width: "100%",
          aspectRatio: "1 / 1",
          overflow: "hidden",
          backgroundColor: "var(--chakra-colors-border-subtle)",
          cursor: "pointer",
        }}
      >
        <MediaBlobImage
          path={hero}
          alt={participant.firstName ?? "Profile"}
          css={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          fallback={
            <Stack align="center" gap="2" px="3" textAlign="center">
              <Icon name="user-solid" boxSize="40%" />
              <chakra.p
                fontSize="xs"
                fontWeight="medium"
                m="0"
                color="fg.brand"
              >
                No photos yet
              </chakra.p>
            </Stack>
          }
        />

        <Box
          position="absolute"
          inset="0"
          background="linear-gradient(rgba(0,0,0,0) 55%, rgba(0,0,0,.03) 68%, rgba(0,0,0,.1) 78%, rgba(0,0,0,.3) 88%, rgba(0,0,0,.55) 95%, rgba(0,0,0,.75) 100%)"
          pointerEvents="none"
        />
        <chakra.span
          position="absolute"
          left="3"
          bottom="2"
          color="fg.onBrand"
          fontWeight="bold"
          fontSize="sm"
        >
          {participant.firstName}
          {participant.age != null ? `, ${participant.age}` : ""}
        </chakra.span>
      </MChakraDiv>
      <Box p="2">
        <chakra.button
          type="button"
          onClick={() => onTileAction(participant)}
          aria-pressed={isFilled}
          w="full"
          h="32px"
          borderRadius="full"
          borderWidth="1px"
          fontSize="sm"
          fontWeight="semibold"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="1"
          cursor="pointer"
          {...(isFilled
            ? { bg: tone.bg, color: "fg.onBrand", borderColor: tone.bg }
            : { bg: "bg.canvas", color: tone.fg, borderColor: tone.border })}
        >
          {icon && <Icon name={icon} boxSize="12px" />}
          {label}
        </chakra.button>
      </Box>
    </Box>
  );
}

function tileButtonLabel(
  status: SparkParticipantStatus | null,
  category: "dating" | "friend"
): string {
  if (status === SparkParticipantStatus.MutualSpark) {
    return category === "dating" ? "Chat with Match" : "Chat with Friend";
  }
  if (status === SparkParticipantStatus.Spark) {
    return category === "dating" ? "Liked!" : "Friend Requested!";
  }
  return category === "dating" ? "Like" : "Friend";
}

function tileButtonIcon(
  status: SparkParticipantStatus | null,
  category: "dating" | "friend"
): "heart" | "smile" | null {
  if (
    status === SparkParticipantStatus.Spark ||
    status === SparkParticipantStatus.MutualSpark
  ) {
    return null;
  }
  return category === "dating" ? "heart" : "smile";
}

// ---------------------------------------------------------------------------
// Profile preview modal — opened from a grid tile tap. Shows the full
// MatchProfile with the same decision buttons as the profile view.
// ---------------------------------------------------------------------------

function ProfilePreviewModal({
  profile,
  category,
  onClose,
  onDecision,
}: {
  profile: UserProfile;
  category: "dating" | "friend";
  onClose: () => void;
  onDecision: (decision: MatchProfileDecision) => void;
}) {
  return (
    <Box
      position="fixed"
      inset="0"
      zIndex="40"
      display="flex"
      alignItems="stretch"
      justifyContent="center"
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)",
        paddingLeft: "calc(env(safe-area-inset-left) + 16px)",
        paddingRight: "calc(env(safe-area-inset-right) + 16px)",
      }}
    >
      <MChakraDiv
        aria-label="Close profile preview"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
        }}
      />
      <MChakraDiv
        key={`profile-preview-${profile.id}`}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={modalVariants}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          background: "white",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          transformOrigin: "center",
        }}
      >
        <chakra.button
          type="button"
          onClick={onClose}
          aria-label="Close"
          position="absolute"
          top="3"
          right="3"
          zIndex="1"
          w="36px"
          h="36px"
          borderRadius="full"
          bg="bg.canvas"
          borderWidth="1px"
          borderColor="border.subtle"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="fg.default"
          cursor="pointer"
        >
          <Icon name="x" boxSize="14px" />
        </chakra.button>
        <Box flex="1" minH="0" overflowY="auto" pt="4" position="relative">
          <MatchProfile
            profile={profile}
            category={category}
            onDecision={onDecision}
          />
        </Box>
      </MChakraDiv>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Preferences modal — opened from the sliders button next to the search bar.
// Lets the user toggle view layout and configure an age filter.
// ---------------------------------------------------------------------------

function PreferencesModal({
  preferences,
  onChange,
  onClose,
}: {
  preferences: MatchPreferences;
  onChange: (next: Partial<MatchPreferences>) => void;
  onClose: () => void;
}) {
  const { viewMode, ageFilterEnabled, ageRange } = preferences;
  return (
    <Box
      position="fixed"
      inset="0"
      zIndex="40"
      display="flex"
      alignItems="stretch"
      justifyContent="center"
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)",
        paddingLeft: "calc(env(safe-area-inset-left) + 16px)",
        paddingRight: "calc(env(safe-area-inset-right) + 16px)",
      }}
    >
      <MChakraDiv
        aria-label="Close preferences"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
        }}
      />
      <MChakraDiv
        key="preferences-modal"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={modalVariants}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "480px",
          alignSelf: "center",
          background: "white",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          transformOrigin: "center",
        }}
      >
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="56px"
          px="4"
          borderBottomWidth="1px"
          borderColor="border.subtle"
        >
          <chakra.h2 fontSize="lg" fontWeight="semibold" m="0">
            Preferences
          </chakra.h2>
          <chakra.button
            type="button"
            onClick={onClose}
            aria-label="Close"
            position="absolute"
            right="3"
            top="50%"
            transform="translateY(-50%)"
            w="36px"
            h="36px"
            borderRadius="full"
            bg="bg.canvas"
            borderWidth="1px"
            borderColor="border.subtle"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="fg.default"
            cursor="pointer"
          >
            <Icon name="x" boxSize="14px" />
          </chakra.button>
        </Box>

        <Box p="5" display="flex" flexDirection="column" gap="6">
          <Box>
            <chakra.div
              fontSize="sm"
              fontWeight="semibold"
              color="fg.default"
              mb="2"
            >
              Layout
            </chakra.div>
            <ViewModeToggle
              value={viewMode}
              onChange={(next) => onChange({ viewMode: next })}
            />
          </Box>

          <Box>
            <Stack direction="row" align="center" justify="space-between" mb="2">
              <chakra.div fontSize="sm" fontWeight="semibold" color="fg.default">
                Filter by age
              </chakra.div>
              <SwitchToggle
                checked={ageFilterEnabled}
                onChange={(checked) => onChange({ ageFilterEnabled: checked })}
                ariaLabel="Toggle age filter"
              />
            </Stack>
            <Box
              opacity={ageFilterEnabled ? 1 : 0.4}
              pointerEvents={ageFilterEnabled ? "auto" : "none"}
              transition="opacity 0.15s"
            >
              <AgeRangeSlider
                value={ageRange}
                onChange={(next) => onChange({ ageRange: next })}
              />
            </Box>
          </Box>
        </Box>
      </MChakraDiv>
    </Box>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: MatchViewMode;
  onChange: (next: MatchViewMode) => void;
}) {
  return (
    <Box
      display="grid"
      gridTemplateColumns="1fr 1fr"
      gap="0"
      p="1"
      borderRadius="full"
      bg="bg.subtle"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      {(["profile", "grid"] as const).map((mode) => {
        const active = value === mode;
        return (
          <chakra.button
            key={mode}
            type="button"
            onClick={() => onChange(mode)}
            aria-pressed={active}
            h="36px"
            borderRadius="full"
            fontSize="sm"
            fontWeight="semibold"
            textTransform="capitalize"
            cursor="pointer"
            border="none"
            bg={active ? "bg.canvas" : "transparent"}
            color={active ? "fg.default" : "fg.muted"}
            boxShadow={active ? "0 1px 3px rgba(0,0,0,0.08)" : "none"}
            transition="background 0.15s, color 0.15s"
          >
            {mode}
          </chakra.button>
        );
      })}
    </Box>
  );
}

function SwitchToggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <chakra.button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      position="relative"
      w="44px"
      h="26px"
      borderRadius="full"
      border="none"
      cursor="pointer"
      p="0"
      bg={checked ? "bg.brand" : "border.subtle"}
      transition="background 0.15s"
    >
      <chakra.span
        position="absolute"
        top="3px"
        left={checked ? "21px" : "3px"}
        w="20px"
        h="20px"
        borderRadius="full"
        bg="white"
        boxShadow="0 1px 3px rgba(0,0,0,0.25)"
        transition="left 0.15s"
      />
    </chakra.button>
  );
}

// Two overlapping native range inputs. Both sit on top of a shared track;
// the upper thumb is the one whose side of the selection is being dragged.
// We z-index the handle closer to a press so it always wins pointer events
// near the edge.
function AgeRangeSlider({
  value,
  onChange,
}: {
  value: [number, number];
  onChange: (next: [number, number]) => void;
}) {
  const [lo, hi] = value;
  const pct = (n: number) => ((n - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100;
  const minPct = pct(lo);
  const maxPct = pct(hi);

  return (
    <Box>
      <Stack direction="row" justify="space-between" mb="3">
        <chakra.span fontSize="sm" color="fg.muted">
          {lo}
        </chakra.span>
        <chakra.span fontSize="sm" color="fg.muted">
          {hi === AGE_MAX ? `${hi}+` : hi}
        </chakra.span>
      </Stack>
      <Box position="relative" h="32px">
        <Box
          position="absolute"
          left="0"
          right="0"
          top="50%"
          transform="translateY(-50%)"
          h="4px"
          borderRadius="full"
          bg="border.subtle"
        />
        <Box
          position="absolute"
          top="50%"
          transform="translateY(-50%)"
          h="4px"
          borderRadius="full"
          bg="bg.brand"
          style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }}
        />
        <chakra.input
          type="range"
          min={AGE_MIN}
          max={AGE_MAX}
          value={lo}
          onChange={(e) => {
            const next = Math.min(Number(e.target.value), hi);
            onChange([next, hi]);
          }}
          aria-label="Minimum age"
          className="gf-age-range"
          position="absolute"
          inset="0"
          w="100%"
          h="32px"
          bg="transparent"
          pointerEvents="none"
          style={{ WebkitAppearance: "none", appearance: "none" }}
        />
        <chakra.input
          type="range"
          min={AGE_MIN}
          max={AGE_MAX}
          value={hi}
          onChange={(e) => {
            const next = Math.max(Number(e.target.value), lo);
            onChange([lo, next]);
          }}
          aria-label="Maximum age"
          className="gf-age-range"
          position="absolute"
          inset="0"
          w="100%"
          h="32px"
          bg="transparent"
          pointerEvents="none"
          style={{ WebkitAppearance: "none", appearance: "none" }}
        />
        <AgeRangeSliderStyles />
      </Box>
    </Box>
  );
}

// Scoped styles for the dual-thumb range slider. Chakra's `chakra.input` can't
// target the WebKit/Mozilla pseudo-elements, so we inject a small <style> block
// beside the inputs. The track is transparent (we draw it in the Box above);
// only thumbs receive pointer events so both inputs can coexist.
function AgeRangeSliderStyles() {
  return (
    <chakra.style>{`
      .gf-age-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        pointer-events: auto;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #fff;
        border: 2px solid var(--chakra-colors-bg-brand);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        cursor: pointer;
      }
      .gf-age-range::-moz-range-thumb {
        pointer-events: auto;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #fff;
        border: 2px solid var(--chakra-colors-bg-brand);
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        cursor: pointer;
      }
      .gf-age-range::-webkit-slider-runnable-track {
        background: transparent;
        height: 4px;
      }
      .gf-age-range::-moz-range-track {
        background: transparent;
        height: 4px;
      }
      .gf-age-range:focus {
        outline: none;
      }
    `}</chakra.style>
  );
}
